import React from "react";

import { useFormContext } from "~/components/form/use-form-context";
import { useFormFieldContext } from "~/components/form/use-form-field-context";
import { type VariantProps, cv } from "~/tools/class-variance";
import { type IgnorePasswordManagers, ignorePasswordManagerAttributes } from "~/tools/ignore-password-managers";
import { useScoped } from "~/tools/impulse";
import { useOnInvalidFormFocus } from "~/tools/impulse-form/use-on-invalid-form-focus";
import { mergeRefs } from "~/tools/merge-refs";

import styles from "./_textarea.module.css";

/**
 * The visual design is inspired by the Input component, see Figma:
 *
 * @link https://www.figma.com/design/YIkZms7ygvgqXYnbzDFr8Y/%23-StackState-DS?node-id=4347-1200&t=pnGLA5EyfwG7fG1F-4
 */
const variants = cv(styles.textarea, {
	variants: {
		variant: {
			neutral: styles.neutral,
			error: styles.error,
		},
		size: {
			sm: styles.sm,
			md: styles.md,
			lg: styles.lg,
		},
	},
	defaultVariants: {
		variant: "neutral",
		size: "md",
	},
});

export interface TextareaProps
	extends IgnorePasswordManagers,
		VariantProps<typeof variants>,
		Omit<React.ComponentProps<"textarea">, "size"> {
	loading?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
	({ allowPasswordManager, className, variant, size, loading, disabled, readOnly, onBlur, ...props }, ref) => {
		const form = useFormContext();
		const formField = useFormFieldContext();
		const focusRef = useOnInvalidFormFocus(formField?.state);
		const submitting = useScoped((scope) => formField?.state.isSubmitting(scope), [formField?.state]);
		const invalid = useScoped((scope) => formField?.state.isInvalid(scope), [formField?.state]);

		return (
			<textarea
				aria-busy={submitting || loading}
				aria-disabled={disabled}
				className={variants({ variant: invalid ? "error" : variant, size, className })}
				form={form?.id}
				id={formField?.id}
				onBlur={(event) => {
					formField?.state.setTouched(true);
					onBlur?.(event);
				}}
				readOnly={submitting || loading || disabled || readOnly}
				ref={mergeRefs([ref, focusRef])}
				{...ignorePasswordManagerAttributes(allowPasswordManager)}
				{...props}
			/>
		);
	},
);

if (import.meta.env.DEV) {
	Textarea.displayName = "Textarea";
}
